import { __ } from '@wordpress/i18n'
import { useBookingContext } from '../../../providers/BookingFormProvider/BookingFormProvider'
import { useMemo } from 'react'
import './ThankYou.scss'
import iconCheck from '../../../../../public/images/icon-check.svg'
import { BookingBlock } from '../../../components/BookingBlock/BookingBlock'
import {
    wbkCalculateAmount,
    wbkFormatPrice,
} from '../../../providers/BookingFormProvider/utils'
import { Button } from '../../../components/Button/Button'
import { IServiceProps } from '../../../components/Services/types'
import classNames from 'classnames'
import { FormNotice } from '../../../components/FormNotice/FormNotice'
import { useSelect } from '@wordpress/data'
import { store_name } from '../../../../store/frontend'
import { CustomScroll } from 'react-custom-scroll'
import { useWording } from '../../../hooks/useWording'
import { fromUnixTime } from 'date-fns'
import { fromZonedTime, toZonedTime } from 'date-fns-tz'

interface ThankYouProps {
    bookingDetails?: any
}

export const ThankYou = ({
    bookingDetails: propBookingDetails,
}: ThankYouProps = {}) => {
    const {
        services,
        priceFormat,
        amountData,
        preset,
        timezone,
        userTimezone,
    } = useBookingContext()
    const wording = useWording()
    const selectedServices = useMemo(
        () => services.filter(({ selected }) => selected),
        [services]
    )

    const cartItems = useMemo(
        () =>
            selectedServices.flatMap(
                ({ places, label }) =>
                    places?.map(({ timeslot }) => {
                        return {
                            time:
                                Number(
                                    toZonedTime(
                                        fromUnixTime(Number(timeslot)),
                                        timezone
                                    )
                                ) / 1000,
                            serviceName: String(label),
                        }
                    }) || []
            ),
        [selectedServices]
    )
    const totalAmountFromContext = useMemo(
        () => amountData.total || 0,
        [amountData]
    )

    // Get bookingData from store
    const bookingDataFromStore = useSelect(
        (select: any) => select(store_name).getBookingData(),
        []
    )
    const bookingData =
        bookingDataFromStore && Object.keys(bookingDataFromStore).length > 0
            ? bookingDataFromStore
            : propBookingDetails
    const message = bookingData?.message

    // Booking items and cost breakdown
    let bookingItems = cartItems

    // --- Dual Calculation for Cost Breakdown ---
    // 1. Use amountData.items if available
    // 2. Fallback: service price * quantity * number of selected places
    let costItems = selectedServices.map((service) => {
        // Find all amountData items for this service
        const matchedAmountItems = amountData.items.filter(
            (item) => item.id === service.id
        )
        // Sum up the price from amountData for this service
        const amountDataPrice = matchedAmountItems.reduce(
            (sum, item) => sum + Number(item.price || 0),
            0
        )
        // Fallback calculation: service price * quantity * number of selected places
        const servicePrice = Number(service.price) || 0
        const quantity = Number(service.quantity) || 1
        const numPlaces = Array.isArray(service.places)
            ? service.places.length
            : 1
        const fallbackPrice = servicePrice * quantity * numPlaces
        return {
            name: service.label,
            price: amountDataPrice > 0 ? amountDataPrice : fallbackPrice,
            priceFromAmountData: amountDataPrice,
            priceFallback: fallbackPrice,
        }
    })
    // Total amount: use amountData.total if available, else sum fallback
    let totalAmount =
        amountData.total && Number(amountData.total) > 0
            ? Number(amountData.total)
            : costItems.reduce((sum, item) => sum + item.priceFallback, 0)

    if (
        bookingData &&
        bookingData.booking_data &&
        bookingData.payment_details
    ) {
        bookingItems = Object.values(bookingData.booking_data).map(
            (item: any) => ({
                time: new Date(Number(item.time) * 1000),
                serviceName: String(item.service),
            })
        )
        const names: string[] = bookingData.payment_details.item_names || []
        const prices: (string | number)[] =
            bookingData.payment_details.prices || []
        const quantities = bookingData.payment_details.quantities || []
        costItems = names.map((name: string, idx: number) => {
            const price =
                Number(prices[idx] || 0) * Number(quantities[idx] || 1)
            return {
                name,
                price,
                priceFromAmountData: price,
                priceFallback: price, // fallback is not meaningful here, but keep shape
            }
        })
        totalAmount = bookingData.payment_details.total || 0
    }

    return (
        <div className={'wbk_thank_you__wrapper'}>
            <CustomScroll
                heightRelativeToParent="calc(750px - 64px)"
                className={'wbk_thank_you__custom-scroll'}
            >
                <div className={'wbk_thank_you__inner-wrapper'}>
                    <div className={'wbk_thank_you__header'}>
                        <img
                            src={iconCheck}
                            alt={
                                wording?.booking_success ||
                                __('Booking success', 'webba-booking-lite')
                            }
                        />
                        <div className={'wbk_thank_you__header__content'}>
                            <h1>
                                {wording?.appointment_confirmed ||
                                    __(
                                        'Appointment Confirmed',
                                        'webba-booking-lite'
                                    )}
                            </h1>
                            <p>
                                {wording?.look_forward_seeing_you ||
                                    __(
                                        'We look forward to seeing you.',
                                        'webba-booking-lite'
                                    )}
                            </p>
                        </div>
                    </div>
                    {message && (
                        <FormNotice className={'wbk_thank_you__information'}>
                            <h4>
                                {wording?.payment_information ||
                                    __(
                                        'Payment Information',
                                        'webba-booking-lite'
                                    )}
                            </h4>
                            <p>{message}</p>
                        </FormNotice>
                    )}
                    <div className={'wbk_thank_you__booking_items'}>
                        {bookingItems.map(
                            ({ time, serviceName }: any, idx: number) =>
                                costItems[idx]?.price > 0 && (
                                    <BookingBlock
                                        key={idx}
                                        time={Number(time)}
                                        serviceName={serviceName}
                                    />
                                )
                        )}
                    </div>
                    {bookingData?.ical_url && preset?.is_pro === true && (
                        <Button
                            title={
                                wording?.add_to_calendar ||
                                __('+ Add to Calendar', 'webba-booking-lite')
                            }
                            href={bookingData.ical_url}
                            target="_blank"
                            classes={'wbk_thank_you__button-calendar-link'}
                        />
                    )}
                    <div className={'wbk_thank_you__panel-wrapper'}>
                        <h4>
                            {wording?.cost_breakdown ||
                                __('Cost Breakdown', 'webba-booking-lite')}
                        </h4>
                        <div className={'wbk_thank_you__cart-items'}>
                            {costItems.map((item, idx) => (
                                <div
                                    className={
                                        'wbk_thank_you__cart-items__item'
                                    }
                                    key={idx}
                                >
                                    <p>{item.name}</p>
                                    <strong>
                                        {item.priceFromAmountData > 0
                                            ? wbkFormatPrice(
                                                  item.priceFromAmountData,
                                                  priceFormat
                                              )
                                            : wbkFormatPrice(
                                                  item.priceFallback,
                                                  priceFormat
                                              )}
                                    </strong>
                                </div>
                            ))}
                            {/* Show tax if greater than 0 */}
                            {typeof amountData.tax === 'number' &&
                                amountData.tax > 0 && (
                                    <div
                                        className={classNames(
                                            'wbk_thank_you__cart-items__item',
                                            'wbk_thank_you__cart-items__item--tax'
                                        )}
                                    >
                                        <h4>
                                            {wording?.tax ||
                                                __('Tax', 'webba-booking-lite')}
                                        </h4>
                                        <strong>
                                            {wbkFormatPrice(
                                                amountData.tax,
                                                priceFormat
                                            )}
                                        </strong>
                                    </div>
                                )}
                            <div
                                className={classNames(
                                    'wbk_thank_you__cart-items__item',
                                    'wbk_thank_you__cart-items__item--total'
                                )}
                            >
                                <h4>
                                    {wording?.total_amount_paid ||
                                        __(
                                            'Total amount paid',
                                            'webba-booking-lite'
                                        )}
                                </h4>
                                <strong>
                                    {wbkFormatPrice(totalAmount, priceFormat)}
                                </strong>
                            </div>
                        </div>
                    </div>
                    {wording?.after_booking_instructions && (
                        <FormNotice className={'wbk_thank_you__information'}>
                            <div
                                dangerouslySetInnerHTML={{
                                    __html: wording?.after_booking_instructions,
                                }}
                            />
                        </FormNotice>
                    )}
                </div>
            </CustomScroll>
        </div>
    )
}
